<script setup lang="ts">
/**
 * Client name cell for the public clients statement table: row number beside
 * the name, then the client's latest balance below — mirrors
 * `PerformerNameCell`'s "index inline with name, stat summary below" layout,
 * trimmed to the one stat this report has.
 */
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

defineProps<{
  /** 1-based row index (stable across the loaded/filtered list). */
  rowNumber: number
  /** Client display name. */
  name: string
  /** Pre-formatted latest balance, e.g. "$1,907.00" or "–" when unknown. */
  balanceFormatted: string
  /** Sign of the raw balance amount, for coloring; `undefined` when unknown. */
  balanceSign?: 'positive' | 'negative' | 'zero'
  /** Max width of the cell (CSS value). Set by DynamicTable from column config. */
  maxWidth?: string
}>()
</script>

<template>
  <td
    class="client-statement-name-cell text-tufte-text font-tufte"
    :style="maxWidth ? { maxWidth: maxWidth, overflowWrap: 'break-word' } : undefined"
  >
    <div class="client-statement-name-cell__header">
      <span class="client-statement-name-cell__index">{{ rowNumber }}</span>
      <span class="client-statement-name-cell__name">{{ name }}</span>
    </div>
    <div class="client-statement-name-cell__stat-row">
      <span class="client-statement-name-cell__lbl">{{ t('common.balance') }}</span>
      <span
        class="client-statement-name-cell__val"
        :class="{
          'client-statement-name-cell__val--positive': balanceSign === 'positive',
          'client-statement-name-cell__val--negative': balanceSign === 'negative',
        }"
        >{{ balanceFormatted }}</span
      >
    </div>
  </td>
</template>

<style scoped>
.client-statement-name-cell {
  font-size: var(--text-tufte-sm);
  vertical-align: top;
}

.client-statement-name-cell__header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.3rem;
}

.client-statement-name-cell__index {
  font-size: var(--text-tufte-sm);
  color: var(--color-tufte-muted);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
}

.client-statement-name-cell__stat-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  font-family: var(--font-tufte-sans);
}

.client-statement-name-cell__lbl {
  font-size: var(--text-tufte-xs);
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--color-tufte-muted);
}

.client-statement-name-cell__val {
  font-family: var(--font-tufte-mono);
  font-size: var(--text-tufte-sm);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--color-tufte-text);
}

.client-statement-name-cell__val--positive {
  color: var(--color-money-positive);
}

.client-statement-name-cell__val--negative {
  color: var(--color-money-negative);
}
</style>
